/*
================================================================ 
         Copyright (c) 2018-2020 Digitize Things Inc.           
                     All rights reserved.                       
================================================================ 
*/

// store.js
import { createStore, combineReducers, applyMiddleware } from "redux";
import {
  createStateSyncMiddleware,
  initMessageListener,
} from "redux-state-sync";

import cartReducer from "./reducers/cartReducer";
import authReducer from "./reducers/authReducer";
import globalComponentReducer from "./reducers/globalComponentReducer";
import quoteReducer from "./reducers/quoteReducer";

const rootReducer = combineReducers({
  cart: cartReducer,
  auth: authReducer,
  global: globalComponentReducer,
  quote: quoteReducer,
});

/**
 * Sync only LOGOUT across tabs
 * No need to sync every redux action
 */
const syncConfig = {
  channel: "redux_state_channel_v1",
  broadcastChannelOption: {
    type: "localstorage",
  },
  whitelist: ["LOGOUT"], // only sync logout
};

/**
 * Load Redux state from localStorage (if exists)
 */
const loadState = () => {
  try {
    const serialized = localStorage.getItem("reduxState");
    if (!serialized) return undefined;
    return JSON.parse(serialized);
  } catch (e) {
    console.warn("Failed to load persisted Redux state:", e);
    return undefined;
  }
};

/**
 * Save Redux state to localStorage
 */
const saveState = (state) => {
  try {
    localStorage.setItem("reduxState", JSON.stringify(state));
  } catch (e) {
    console.warn("Failed to save Redux state:", e);
  }
};

const middlewares = [createStateSyncMiddleware(syncConfig)];

const store = createStore(
  rootReducer,
  loadState(),
  applyMiddleware(...middlewares)
);

initMessageListener(store);

/**
 * Sync storage update — logout from other tabs
 */
window.addEventListener("storage", (event) => {
  if (event.key === "LOGOUT_EVENT") {
    console.log("LOGOUT_EVENT detected in another tab → resetting store");
    store.dispatch({ type: "LOGOUT" }); // don't sync again — whitelist avoids loop
    localStorage.removeItem("reduxState");
    window.location.href = '/';
  }
});

/**
 * Handle persistence — save only when authenticated
 * Prevent looping by NOT broadcasting logout here
 */
store.subscribe(() => {
  const state = store.getState();

  // If logged out → clear saved Redux, don't broadcast again
  if (!state.auth?.authenticated) {
    localStorage.removeItem("reduxState");
    return;
  }

  // Save state only when logged in
  saveState(state);
});

export default store;

